<template>
  <v-card outlined class="d-flex flex-column fill-height" elevation="0">
    <!-- Header -->
    <v-card-title class="d-flex align-center py-3 px-4" style="height: 64px">
      <div class="text-subtitle-1 text--secondary">Pricing Status</div>
      <v-spacer></v-spacer>
      <v-btn icon small @click="update">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Summary Section -->
    <div
      class="px-4 py-3 lighten-5 d-flex align-center justify-space-between"
      style="height: 80px"
      v-if="!loading && totalProducts"
    >
      <div>
        <span class="text-h5 font-weight-bold primary--text">{{ totalProducts }}</span>
        <span class="text-caption text--secondary ml-1">products in total</span>
      </div>
    </div>

    <!-- Content Area -->
    <v-card-text
      class="flex-grow-1 d-flex flex-column justify-center relative pa-4"
      style="min-height: 250px"
    >
      <!-- Loading State -->
      <div v-if="loading" class="d-flex flex-column align-center justify-center fill-height">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <div class="mt-3 text-caption text--secondary">Loading pricing status data...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!totalProducts" class="text-center my-auto">
        <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-chart-pie</v-icon>
        <div class="text-subtitle-1 text--secondary font-weight-medium">No Data Available</div>
        <div class="text-caption text--secondary mt-1">
          Add matched products to see pricing status
        </div>
      </div>

      <!-- Content -->
      <v-row v-else align="center" class="fill-height">
        <v-col cols="12" sm="5" class="d-flex justify-center">
          <div
            :style="{
              position: 'relative',
              height: chartSize + 'px',
              width: chartSize + 'px',
            }"
          >
            <canvas id="availabilitychart" ref="availabilitychart"></canvas>
          </div>
        </v-col>
        <v-col cols="12" sm="7">
          <v-simple-table class="transparent">
            <template v-slot:default>
              <tbody>
                <tr v-for="(item, index) in legendItems" :key="index">
                  <td class="px-0">
                    <div class="d-flex align-center">
                      <v-icon :color="item.color" small>mdi-circle</v-icon>
                      <span class="ml-2" :class="fontSizeClass">{{ item.label }}</span>
                    </div>
                  </td>
                  <td class="text-right font-weight-bold px-0" :class="fontSizeClass">
                    <div>{{ item.count }} products</div>
                    <div class="d-flex justify-end mt-1">
                      <div
                        :style="{
                          width: (item.count / maxCount) * 100 + 'px',
                          height: '6px',
                          backgroundColor: item.color,
                          borderRadius: '2px',
                        }"
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider v-if="!loading && totalProducts"></v-divider>

    <!-- Footer -->
    <v-card-actions class="px-4 py-2 lighten-5" v-if="!loading && totalProducts">
      <v-spacer></v-spacer>
      <div class="text-caption text--secondary d-flex align-center">
        Updated {{ formattedLastUpdate }}
      </div>
    </v-card-actions>
  </v-card>
</template>
<script>
  import Chart from 'chart.js'
  import axios from 'axios'

  export default {
    name: 'ChartPricingStatus',
    props: ['component_name', 'cols'],
    data() {
      return {
        loading: false,
        chartInstance: null,
        statistics: {
          data: [],
          lastUpdateDate: '',
          total: {
            value: 0,
          },
          price_index: 0,
          pricing: {
            overPriced: {
              count: 0,
              purcentage: 0,
            },
            inlinePrice: {
              count: 0,
              purcentage: 0,
            },
            underPriced: {
              count: 0,
              purcentage: 0,
            },
          },
        },
      }
    },
    computed: {
      formattedLastUpdate() {
        if (!this.statistics.lastUpdateDate) return ''
        return this.dateToDayName(this.statistics.lastUpdateDate)
      },
      legendItems() {
        return [
          {
            label: 'In line',
            count: this.statistics.pricing.inlinePrice.count,
            color: '#20c2de',
          },
          {
            label: 'Overpriced',
            count: this.statistics.pricing.overPriced.count,
            color: '#f96654',
          },
          {
            label: 'Underpriced',
            count: this.statistics.pricing.underPriced.count,
            color: '#42b983',
          },
        ]
      },
      maxCount() {
        const counts = [
          this.statistics.pricing.inlinePrice.count,
          this.statistics.pricing.overPriced.count,
          this.statistics.pricing.underPriced.count,
        ]
        return Math.max(...counts) || 1
      },
      chartSize() {
        if (this.cols && this.cols <= 3) return 100
        return 140
      },
      fontSizeClass() {
        return this.cols && this.cols <= 3 ? 'text-caption' : 'text-body-2'
      },
      totalProducts() {
        return (
          this.statistics.pricing.inlinePrice.count +
          this.statistics.pricing.overPriced.count +
          this.statistics.pricing.underPriced.count
        )
      },
    },
    mounted() {
      this.update()
    },
    methods: {
      dateToDayName(date) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        const dateParts = date.split('-')
        const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])

        const today = new Date()
        const yesterday = new Date(new Date().setDate(new Date().getDate() - 1))

        if (dateObj.toDateString() === today.toDateString()) return 'Today'
        if (dateObj.toDateString() === yesterday.toDateString()) return 'Yesterday'
        return days[dateObj.getDay()]
      },

      async update() {
        await this.getStatistics()

        // Process data buckets
        // Reset counts first to avoid accumulation if called multiple times
        this.statistics.pricing.overPriced.count = 0
        this.statistics.pricing.underPriced.count = 0
        this.statistics.pricing.inlinePrice.count = 0

        this.statistics.data.forEach((bucket) => {
          if (bucket.key > 0) {
            this.statistics.pricing.overPriced.count += bucket.doc_count
          } else if (bucket.key < 0) {
            this.statistics.pricing.underPriced.count += bucket.doc_count
          } else {
            this.statistics.pricing.inlinePrice.count += bucket.doc_count
          }
        })

        // Wait for next tick to ensure DOM is updated
        await this.$nextTick()

        const ctx = this.$refs.availabilitychart
        if (!ctx) return

        if (this.chartInstance) {
          this.chartInstance.destroy()
        }

        this.chartInstance = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['In line', 'Overpriced', 'Underpriced'],
            datasets: [
              {
                data: [
                  this.statistics.pricing.inlinePrice.count,
                  this.statistics.pricing.overPriced.count,
                  this.statistics.pricing.underPriced.count,
                ],
                backgroundColor: ['#20c2de', '#f96654', '#42b983'],
                hoverBackgroundColor: ['#20c2de', '#f96654', '#42b983'],
                borderWidth: 0,
              },
            ],
          },
          options: {
            cutoutPercentage: 70,
            responsive: true,
            maintainAspectRatio: false,
            legend: {
              display: false,
            },
            tooltips: {
              enabled: true,
            },
          },
        })
      },

      async getStatistics() {
        this.loading = true
        try {
          const res = await axios.get(`/api/products/statisticsglobalstat`, {
            params: {
              account: localStorage.getItem('account_id'),
              updateInterval: localStorage.getItem('updateInterval') || 1,
              user: localStorage.getItem('user_id'),
              component: this.component_name,
              search_filter: localStorage.getItem('filter'),
              match: 1,
            },
          })
          this.statistics.lastUpdateDate = res.data.lastUpdateDate
          this.statistics.total.value = res.data.total.value
          this.statistics.data = res.data.statisticsData
        } catch (error) {
          console.error(error)
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style scoped></style>
